import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import type { Permission } from '@homework/shared';
import { useAuth } from '../lib/auth';
import { EmptyState, PageLoader } from './ui';

/** Sends unauthenticated visitors to the login screen, remembering where they were. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();

  if (status === 'loading') return <PageLoader />;
  if (status === 'anonymous') {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  return <>{children}</>;
}

/**
 * Hides a screen the user has no permission for. The server enforces the same
 * rule on every request — this only avoids showing a page that would 403.
 *
 * Accepts a list when a screen serves more than one role: the leave-policy
 * editor lives on the admin settings page but is HR's to use, so that route
 * admits either permission and the page disables what the viewer cannot do.
 */
export function RequirePermission({
  permission,
  children,
}: {
  permission: Permission | Permission[];
  children: ReactNode;
}) {
  const { can } = useAuth();
  const allowed = Array.isArray(permission) ? permission.some(can) : can(permission);
  if (!allowed) {
    return (
      <EmptyState
        icon={ShieldAlert}
        title="You do not have access to this page"
        body="If you think you should, ask an admin or HR to check your role."
      />
    );
  }
  return <>{children}</>;
}
